import React from "react"
import Icon from "./Icon"

const PayScoreGauge = ({ score, label = "PaySCORE", status: forcedStatus = null, color: forcedColor = null, showUpdated = false }) => {
  const numericScore = Number(score);
  const hasScore = Number.isFinite(numericScore);
  const value = hasScore ? Math.max(0, Math.min(100, numericScore)) : 0;
  const rotation = (value / 100) * 180 - 90; // -90 to 90 degrees for a semi-circle
  const arcPath = "M10 50 A40 40 0 0 1 90 50";

  // Segment ranges to match the legend:
  // Default: 0-50 (Red)
  // Risk: 50-70 (Orange)
  // Fair: 70-80 (Amber)
  // Good: 80-90 (Light Green)
  // Excellent: 90-100 (Emerald)
  const segments = [
    { color: "#ef4444", length: 50, start: 0 },
    { color: "#f97316", length: 20, start: 50 },
    { color: "#f59e0b", length: 10, start: 70 },
    { color: "#84cc16", length: 10, start: 80 },
    { color: "#10b981", length: 10, start: 90 },
  ];

  const segmentGap = 0.5; // Smaller gap for cleaner look with unequal segments

  // Determine status color based on score
  const getStatusColor = (s) => {
    if (s >= 90) return "#10b981"; // Excellent (Emerald)
    if (s >= 80) return "#84cc16"; // Good (Light Green)
    if (s >= 70) return "#f59e0b"; // Fair (Amber)
    if (s >= 50) return "#f97316"; // Risk (Orange)
    return "#ef4444"; // Default (Red)
  };

  const getStatus = (v, forced) => {
    if (forced) {
      const f = String(forced).toUpperCase();
      if (f === 'DEFAULT' || f === 'N/A' || f === 'NONE' || f === '--') return 'N/A';
      return forced;
    }
    if (!hasScore || v === 0) return 'N/A';
    if (v >= 90) return 'Excellent';
    if (v >= 80) return 'Good';
    if (v >= 70) return 'Fair';
    if (v >= 50) return 'Risk';
    return 'Default';
  };

  const status = getStatus(value, forcedStatus);
  const isNA = status === 'N/A';
  const color = forcedColor || (!isNA && hasScore ? getStatusColor(value) : "#9ca3af");

  return (
    <div className="relative w-full max-w-[150px] h-[92px] sm:max-w-[200px] sm:h-[120px] mx-auto flex flex-col items-center">
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 65">
        {/* Gauge background arc */}
        <path d={arcPath} fill="none" stroke="#eef2f7" strokeWidth="8" strokeLinecap="round" />
        
        {/* Colored segments */}
        {segments.map((seg, index) => (
          <path
            key={`${seg.color}-${index}`}
            d={arcPath}
            fill="none"
            stroke={isNA ? "#e2e8f0" : seg.color}
            strokeWidth="8"
            strokeLinecap="butt"
            pathLength="100"
            strokeDasharray={`${seg.length - (index < segments.length - 1 ? segmentGap : 0)} 100`}
            strokeDashoffset={-seg.start}
          />
        ))}

        {/* Needle */}
        {!isNA && (
          <g style={{ transform: `rotate(${rotation}deg)`, transformOrigin: '50px 50px', transition: 'transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
            <line x1="50" y1="50" x2="50" y2="15" stroke="#374151" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="50" cy="50" r="3.5" fill="#374151" stroke="white" strokeWidth="1" />
          </g>
        )}

        {/* Scale labels */}
        <text x="8" y="60" fontSize="5" fontWeight="bold" fill="#94a3b8">0</text>
        <text x="86" y="60" fontSize="5" fontWeight="bold" fill="#94a3b8">100</text>
      </svg>

      <div className="absolute top-[30px] sm:top-[35px] inset-x-0 text-center flex flex-col items-center">
        <p className="text-[8px] sm:text-[9px] font-bold text-slate-400 uppercase tracking-tight mb-0">{label}</p>
        <p className="text-2xl sm:text-3xl font-black text-slate-800 leading-none">{(!isNA && hasScore) ? value.toFixed(0) : "--"}</p>
        <div
          className="mt-1 px-2 py-0.5 rounded-full text-[8px] sm:text-[9px] font-black uppercase tracking-wider text-white shadow-sm inline-block"
          style={{ backgroundColor: color }}
        >
          {status}
        </div>
        {showUpdated && (
          <p className="text-[7px] text-slate-400 mt-1 font-bold hidden sm:block">Updated just now</p>
        )}
      </div>
    </div>
  );
};

export default PayScoreGauge
